import React, { useState, useEffect, Fragment } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import { Typography, TextField } from "@mui/material";
import { IVulnerability } from "./modal";
import { addVulnerability, editVulnerability } from "./helper";
import DialogBoxComponent from "../../../components/DialogBoxComponent";
import { useUserContext } from "../../../context/auth/UserContext";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const getTypographyStyles = () => ({
  //Typography styles
  fontWeight: 400,
  fontSize: "14px",
  fontStyle: "italic",
  opacity: 0.5,
});

interface VulnerabilityFormProps {
  openDialogBox: boolean;
  list: IVulnerability[];
  selectedRow?: IVulnerability | null;
  handleCancel: () => void;
  handleRefresh: () => void;
}

export const VulnerabilityForm: React.FC<VulnerabilityFormProps> = (props) => {
  const [open, setOpen] = useState(false);
  const { userId, companyProfileID } = useUserContext();

  const [formTitle, setFormTitle] = useState<string>("Create Vulnerability");
  const [vulnerabilityName, setVulnerabilityName] = useState<string>("");
  const [validationMessage, setValidationMessage] = useState<string>("")

  const [dialogState, setDialogState] = useState({
    title: "",
    body: "",
    action: "",
    actionCancel: "",
    openSuccessDialogBox: false,
  });

  const disableForm = () => {
    return vulnerabilityName === "";
  };

  const handleClose = () => {
    setValidationMessage("");
    props.handleCancel();
    setValidationMessage("")
    setVulnerabilityName("")
  };

  const handleCloseDialog = () => {
    if (dialogState.title === "Success") {

      setDialogState({
        ...dialogState,
        openSuccessDialogBox: false,
      });
    } else {
      setDialogState({
        ...dialogState,
        openSuccessDialogBox: false,
      });
    }
  };

  const handleAdd = async () => {
    const isExsisting = props.list.filter(
      (ele) => ele.vulnerabiliteName === vulnerabilityName
    );

    if (isExsisting.length > 0) {
      setValidationMessage(`${vulnerabilityName} already exists.`);
      return;
    } else {
      setValidationMessage("");

    }

    const date = new Date();
    const payload = {
      vulnerabiliteName: vulnerabilityName,
      createDate: date,
      companyProfileID: companyProfileID,
      createdBy: userId,
    };
    const res = await addVulnerability(payload);
    handleClose();
    props.handleRefresh();
    if (res.succeeded === true) {
      setDialogState({
        title: "Success",
        body: "Vulnerability successfully created",
        action: "OK",
        actionCancel: "",
        openSuccessDialogBox: true,
      });
    } else {
      setDialogState({
        title: "Error",
        body: res.error,
        action: "OK",
        actionCancel: "",
        openSuccessDialogBox: true,
      });
    }
  };

  const handleEdit = async () => {
    const isExsisting = props.list.filter(
      (ele) =>
        ele.vulnerabiliteName === vulnerabilityName &&
        ele.vulnerabilityId !== props.selectedRow?.vulnerabilityId
    );
    if (isExsisting.length > 0) {
      setValidationMessage(`${vulnerabilityName} already exists.`);
      return
    } else {
      setValidationMessage("")
    }
    const date = new Date();
    const payload = {
      vulnerabiliteName: vulnerabilityName,
      updateDate: date,
      updatedBy: userId,
      vulnerabilityId: props.selectedRow?.vulnerabilityId,
      companyProfileID: companyProfileID,
    };
    const res = await editVulnerability(payload);
    handleClose();
    props.handleRefresh();
    if (res.succeeded === true) {
      setDialogState({
        title: "Success",
        body: "Vulnerability successfully updated",
        action: "OK",
        actionCancel: "",
        openSuccessDialogBox: true,
      });
    } else {
      setDialogState({
        title: "Error",
        body: res.error,
        action: "OK",
        actionCancel: "",
        openSuccessDialogBox: true,
      });
    }
  };

  const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (props.selectedRow?.vulnerabilityId) {
      handleEdit()
    } else {
      handleAdd()
    }
  }

  useEffect(() => {
    setOpen(props.openDialogBox);
    if (props.selectedRow?.vulnerabilityId) {
      setFormTitle("Edit Vulnerability");
      setVulnerabilityName(props.selectedRow.vulnerabiliteName);
    } else {
      setFormTitle("Create Vulnerability");
      setVulnerabilityName("");
    }
  }, [props.openDialogBox]);

  return (
    <Fragment>
      <Dialog
        open={open}
        fullWidth
        maxWidth="xs"
        sx={{ borderRadius: "40px" }}
        onClose={handleClose}
        TransitionComponent={Transition}
        PaperProps={{
          component: 'form',
          onSubmit: handleFormSubmit
        }}
        keepMounted
      >
        <DialogTitle>{formTitle}</DialogTitle>
        <DialogContent>
          <Typography sx={getTypographyStyles}>Vulnerability Name</Typography>
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            size="small"
            variant="outlined"
            sx={{ backgroundColor: "#E4EBEF" }}
            value={vulnerabilityName}
            onChange={(e) => setVulnerabilityName(e.target.value)}
          />
          {validationMessage && (
            <Typography variant="body2" color="error">
              {validationMessage}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            variant="contained"
            className="table-btn"
            onClick={handleClose}

          >
            Cancel
          </Button>
          {props.selectedRow?.vulnerabilityId ? (
            <Button
              size="small"
              variant="contained"
              sx={{
                backgroundColor: "#0066FF",
                "&:hover": {
                  bgcolor: "#0556d0",
                },
              }}
              disabled={disableForm()}
              //onClick={handleEdit}
              type="submit"
            >
              Edit
            </Button>
          ) : (
            <Button
              size="small"
              variant="contained"
              sx={{
                backgroundColor: "#0066FF",
                "&:hover": {
                  bgcolor: "#0556d0",
                },
              }}
              disabled={disableForm()}
              //onClick={handleAdd}
              type="submit"
            >
              Create
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {
        <DialogBoxComponent
          openDialogBox={dialogState.openSuccessDialogBox}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogBody={dialogState.body}
          handleFunction={handleCloseDialog}
        />
      }
    </Fragment>
  );
};
